import { Subscription } from '../subscribable';
import { SubscribableSleepWakeUp } from '../wakeUp';

export interface NetworkConfig {
  /** Track navigator.onLine; suspend retries while offline. */
  onlineAware: boolean;
  /** Reconnect after laptop wake (SubscribableSleepWakeUp clock-jump worker). */
  wakeUpReconnect: boolean;
  /** Send a clean close frame on pagehide so the server frees the session immediately. */
  closeOnPageHide: boolean;
}

export const DEFAULT_NETWORK_CONFIG: NetworkConfig = {
  closeOnPageHide: true,
  onlineAware: true,
  wakeUpReconnect: true,
};

export interface NetworkMonitorCallbacks {
  onOffline: () => void;
  onOnline: () => void;
  onWake: () => void;
  onPageHide: () => void;
}

/**
 * True only where the DOM event API we rely on actually exists. React Native defines a global
 * `window` object, so a bare `typeof window === 'object'` check passes there and then throws on
 * the first addEventListener call — every entry point below goes through this instead.
 */
const hasWindow = (): boolean => typeof window === 'object' && typeof window.addEventListener === 'function';

export class NetworkMonitor {
  private sleepWakeUp = new SubscribableSleepWakeUp();
  private wakeSubscription?: Subscription<SubscribableSleepWakeUp>;
  private started = false;

  constructor(
    private config: NetworkConfig,
    private callbacks: NetworkMonitorCallbacks,
  ) {}

  public isOnline = (): boolean => {
    if (!this.config.onlineAware || !hasWindow() || typeof window.navigator?.onLine !== 'boolean') {
      return true;
    }
    return window.navigator.onLine;
  };

  public start = (): void => {
    if (this.started || !hasWindow()) {
      return;
    }
    this.started = true;
    if (this.config.onlineAware) {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
    if (this.config.closeOnPageHide) {
      window.addEventListener('pagehide', this.handlePageHide);
    }
    if (this.config.wakeUpReconnect) {
      this.wakeSubscription = this.sleepWakeUp.subscribe(() => this.callbacks.onWake());
    }
  };

  public stop = (): void => {
    if (!this.started) {
      return;
    }
    this.started = false;
    if (hasWindow()) {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
      window.removeEventListener('pagehide', this.handlePageHide);
    }
    this.wakeSubscription?.unsubscribe();
    this.wakeSubscription = undefined;
  };

  private handleOnline = (): void => this.callbacks.onOnline();
  private handleOffline = (): void => this.callbacks.onOffline();
  private handlePageHide = (): void => this.callbacks.onPageHide();
}
